import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Input, Pagination, Select, Spin, Tooltip, Typography, Button } from 'antd';
import { DownOutlined, RadarChartOutlined, RightOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';

const { Text } = Typography;

const DEFAULT_FILTER = {
  parent_types: [],
  child_types: [],
  all_parents: true,
  all_children: true,
  all_statuses: true,
  statuses: [],
  selected_issue_keys: [],
};

const jqlQuote = (value) => (
  `"${String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
);

const buildPreviewJql = (projectKey, filter) => {
  const pk = String(projectKey || '').trim();
  if (!pk) return '';
  const clauses = [`project = ${jqlQuote(pk)}`];
  const keys = (filter?.selected_issue_keys || [])
    .map((k) => String(k).trim())
    .filter(Boolean);
  if (keys.length) {
    clauses.push(`key in (${keys.join(', ')})`);
  }
  if (!filter?.all_statuses) {
    const statuses = (filter?.statuses || [])
      .map((s) => String(s).trim())
      .filter(Boolean);
    if (statuses.length) {
      clauses.push(`status in (${statuses.map(jqlQuote).join(', ')})`);
    }
  }
  return clauses.join(' AND ');
};

const TEXT_PRIMARY = '#1f1f1f';
const TEXT_MUTED = '#595959';
const PAGE_SIZE = 12;
const TREE_ARROW_WIDTH = 14;
const TREE_CHECKBOX_SLOT = 22;
const TREE_MONITOR_SLOT = 22;
const TREE_DEPTH_INDENT = 20;
const TREE_COLUMN_GAP = 8;

const TYPE_COLORS = [
  '#94167f',
  '#1677ff',
  '#52c41a',
  '#fa8c16',
  '#eb2f96',
  '#13c2c2',
  '#722ed1',
  '#faad14',
  '#2f54eb',
  '#a0d911',
];

const panelStyle = {
  flex: 1,
  minWidth: 280,
  border: '1px solid rgba(148, 22, 127, 0.25)',
  borderRadius: 8,
  background: '#faf8fb',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 360,
  maxHeight: 520,
};

const panelBodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 10px 4px',
};

const legendStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 14px',
  padding: '8px 10px',
  marginBottom: 0,
  background: '#fff',
  borderBottom: '1px solid rgba(148, 22, 127, 0.15)',
  borderRadius: '8px 8px 0 0',
  fontSize: 11,
  color: TEXT_MUTED,
};

const filterBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '10px 10px 8px',
  borderBottom: '1px solid rgba(148, 22, 127, 0.1)',
  background: '#faf8fb',
};

const loadingPanelStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 280,
  padding: '40px 24px',
  border: '1px solid rgba(148, 22, 127, 0.2)',
  borderRadius: 8,
  background: 'linear-gradient(180deg, #ffffff 0%, #faf8fb 100%)',
  textAlign: 'center',
};

const sortTreeNodes = (nodes) => {
  nodes.sort((a, b) => a.key.localeCompare(b.key));
  nodes.forEach((node) => sortTreeNodes(node.children));
};

const buildIssueTree = (issues) => {
  const byKey = new Map(
    issues.map((issue) => [issue.key, { ...issue, children: [] }]),
  );
  const roots = [];
  issues.forEach((issue) => {
    const node = byKey.get(issue.key);
    const parentKey = issue.parent_key;
    if (parentKey && byKey.has(parentKey)) {
      byKey.get(parentKey).children.push(node);
    } else {
      roots.push(node);
    }
  });
  sortTreeNodes(roots);
  return roots;
};

const filterTree = (nodes, predicate) => (
  nodes.reduce((acc, node) => {
    const filteredChildren = filterTree(node.children, predicate);
    if (predicate(node) || filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, [])
);

const flattenTree = (nodes) => {
  const rows = [];
  const walk = (list) => {
    list.forEach((node) => {
      rows.push(node);
      walk(node.children);
    });
  };
  walk(nodes);
  return rows;
};

const collectDescendantKeys = (key, childrenByParent) => {
  const result = [];
  const queue = [...(childrenByParent.get(key) || [])];
  while (queue.length) {
    const next = queue.shift();
    result.push(next);
    queue.push(...(childrenByParent.get(next) || []));
  }
  return result;
};

const isSubTaskIssue = (issue) => (
  issue?.generation === 'grandchild'
  || String(issue?.issueType || '').trim().toLowerCase().replace(/\s+/g, ' ') === 'sub-task'
);

const JiraIssueScopePicker = ({
  projectKey,
  credentials,
  value,
  onChange,
  fetchIssueScope,
  disabled = false,
  requireApiToken = true,
  selectedPanelFooter = null,
  monitoredIssueKeys = null,
  onToggleMonitor = null,
  onCheckMonitoredUpdates = null,
  monitorCheckLoading = false,
  monitoredIssueCount = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generations, setGenerations] = useState(null);
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [searchText, setSearchText] = useState('');
  const [collapsedBrowse, setCollapsedBrowse] = useState(() => new Set());
  const [collapsedSelected, setCollapsedSelected] = useState(() => new Set());
  const [browsePage, setBrowsePage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const requestRef = useRef(0);
  const scopeCacheRef = useRef(new Map());

  const filter = useMemo(() => ({
    ...DEFAULT_FILTER,
    ...(value || {}),
    all_statuses: true,
    statuses: [],
  }), [value]);
  const selectedKeys = useMemo(
    () => new Set((filter.selected_issue_keys || []).map((k) => String(k).trim()).filter(Boolean)),
    [filter.selected_issue_keys],
  );

  const previewJql = useMemo(
    () => buildPreviewJql(projectKey, filter),
    [projectKey, filter],
  );

  const loadScope = useCallback(async (nextFilter, { syncFilter = true } = {}) => {
    if (!projectKey || !credentials?.jira_base_url || !credentials?.jira_user_email) {
      return;
    }
    if (requireApiToken && !credentials?.jira_api_token) {
      return;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const payload = {
        jira_base_url: credentials.jira_base_url,
        jira_user_email: credentials.jira_user_email,
        project_key: projectKey,
        issue_type_filter: nextFilter,
      };
      if (credentials.jira_api_token) {
        payload.jira_api_token = credentials.jira_api_token;
      }
      const res = await fetchIssueScope(payload);
      const data = res?.data || res || {};
      if (requestRef.current !== requestId) return;
      const nextGenerations = data.generations || null;
      setGenerations(nextGenerations);
      scopeCacheRef.current.set(projectKey, nextGenerations);
      if (syncFilter && data.issue_type_filter && onChange) {
        const normalizedFilter = {
          ...data.issue_type_filter,
          all_statuses: true,
          statuses: [],
        };
        onChange(normalizedFilter, {
          generations: nextGenerations,
          preview_jql: buildPreviewJql(projectKey, normalizedFilter),
        });
      }
    } catch (err) {
      if (requestRef.current !== requestId) return;
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load Jira issue scope.';
      setError(String(msg));
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [credentials, fetchIssueScope, onChange, projectKey, requireApiToken]);

  useEffect(() => {
    scopeCacheRef.current.clear();
  }, [
    credentials?.jira_base_url,
    credentials?.jira_user_email,
    credentials?.jira_api_token,
  ]);

  useEffect(() => {
    if (!projectKey || !credentials?.jira_base_url || !credentials?.jira_user_email) {
      setGenerations(null);
      setError('');
      setLoading(false);
      return;
    }
    if (requireApiToken && !credentials?.jira_api_token) {
      setGenerations(null);
      setError('');
      setLoading(false);
      return;
    }
    const cachedGenerations = scopeCacheRef.current.get(projectKey);
    if (cachedGenerations) {
      setGenerations(cachedGenerations);
      setError('');
      setLoading(false);
      return;
    }
    loadScope(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectKey,
    credentials?.jira_base_url,
    credentials?.jira_user_email,
    credentials?.jira_api_token,
    requireApiToken,
  ]);

  useEffect(() => {
    setBrowsePage(1);
  }, [typeFilter, searchText]);

  useEffect(() => {
    setSelectedPage(1);
  }, [filter.selected_issue_keys]);

  const parents = generations?.parents || [];
  const children = generations?.children || [];
  const grandchildren = generations?.grandchildren || [];

  const allIssues = useMemo(() => {
    const issues = [];
    const addFromBucket = (bucket, generation) => {
      (bucket || []).forEach((typeRow) => {
        (typeRow.issues || []).forEach((issue) => {
          if (!issue?.key) return;
          issues.push({
            key: issue.key,
            title: issue.title || '',
            parent_key: issue.parent_key || null,
            status: issue.status || '',
            issueType: typeRow.name,
            generation,
          });
        });
      });
    };
    addFromBucket(parents, 'parent');
    addFromBucket(children, 'child');
    addFromBucket(grandchildren, 'grandchild');
    return issues;
  }, [parents, children, grandchildren]);

  const totalIssuesFromApi = useMemo(() => {
    let count = 0;
    const addFromBucket = (bucket) => {
      (bucket || []).forEach((typeRow) => {
        count += (typeRow.issues || []).length;
      });
    };
    addFromBucket(parents);
    addFromBucket(children);
    addFromBucket(grandchildren);
    return count;
  }, [parents, children, grandchildren]);

  const scopeLoaded = !loading && !error;

  const browseEmptyMessage = useMemo(() => {
    if (!scopeLoaded) return '';
    if (allIssues.length === 0) {
      if (totalIssuesFromApi === 0) {
        return `No issues were found in project ${projectKey}.`;
      }
      return `No issues are available in project ${projectKey}.`;
    }
    if (typeFilter !== '__all__' || (searchText || '').trim()) {
      return 'No issues match your search or issue type filter.';
    }
    return 'No issues to display.';
  }, [
    scopeLoaded,
    allIssues.length,
    totalIssuesFromApi,
    projectKey,
    typeFilter,
    searchText,
  ]);

  const issuesByKey = useMemo(
    () => new Map(allIssues.map((issue) => [issue.key, issue])),
    [allIssues],
  );

  const childrenByParent = useMemo(() => {
    const map = new Map();
    allIssues.forEach((issue) => {
      if (!issue.parent_key) return;
      if (!map.has(issue.parent_key)) map.set(issue.parent_key, []);
      map.get(issue.parent_key).push(issue.key);
    });
    return map;
  }, [allIssues]);

  const issueTypeNames = useMemo(() => {
    const names = new Set(allIssues.map((i) => i.issueType));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allIssues]);

  const typeColorMap = useMemo(() => {
    const map = {};
    issueTypeNames.forEach((name, index) => {
      map[name] = TYPE_COLORS[index % TYPE_COLORS.length];
    });
    return map;
  }, [issueTypeNames]);

  const issueMatchesSearch = useCallback((issue, search) => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return true;
    return (
      issue.key.toLowerCase().includes(q)
      || issue.title.toLowerCase().includes(q)
      || String(issue.parent_key || '').toLowerCase().includes(q)
      || issue.issueType.toLowerCase().includes(q)
    );
  }, []);

  const issueMatchesFilter = useCallback((issue) => {
    if (typeFilter !== '__all__' && issue.issueType !== typeFilter) return false;
    return issueMatchesSearch(issue, searchText);
  }, [issueMatchesSearch, searchText, typeFilter]);

  const fullTree = useMemo(() => buildIssueTree(allIssues), [allIssues]);

  const browseTree = useMemo(
    () => filterTree(fullTree, issueMatchesFilter),
    [fullTree, issueMatchesFilter],
  );

  const browseFlat = useMemo(() => flattenTree(browseTree), [browseTree]);

  const selectableBrowseFlat = useMemo(
    () => browseFlat,
    [browseFlat],
  );

  const selectedIssues = useMemo(
    () => allIssues.filter((issue) => selectedKeys.has(issue.key)),
    [allIssues, selectedKeys],
  );

  const selectedTree = useMemo(() => buildIssueTree(selectedIssues), [selectedIssues]);

  const hasSelectedAncestor = useCallback((key) => {
    let current = issuesByKey.get(key);
    while (current?.parent_key) {
      if (selectedKeys.has(current.parent_key)) return true;
      current = issuesByKey.get(current.parent_key);
    }
    return false;
  }, [issuesByKey, selectedKeys]);

  const keysForTypeItem = (item) => (
    (item?.issues || []).map((issue) => issue?.key).filter(Boolean)
  );

  const subtaskKeysForParentKeys = (parentKeys) => {
    const parentSet = new Set(parentKeys);
    return allIssues
      .filter((issue) => issue.generation === 'grandchild' && issue.parent_key && parentSet.has(issue.parent_key))
      .map((issue) => issue.key);
  };

  const keysForTypeItemWithSubtasks = (item) => {
    const typeKeys = keysForTypeItem(item);
    return [...typeKeys, ...subtaskKeysForParentKeys(typeKeys)];
  };

  const applyKeyPatch = (mutate) => {
    const next = new Set(selectedKeys);
    mutate(next);
    return [...next].sort();
  };

  const addWithDescendants = (set, key) => {
    set.add(key);
    collectDescendantKeys(key, childrenByParent).forEach((childKey) => set.add(childKey));
  };

  const removeWithDescendants = (set, key) => {
    set.delete(key);
    collectDescendantKeys(key, childrenByParent).forEach((childKey) => set.delete(childKey));
  };

  const deriveTypeFilterFromKeys = (items, allKey, typesKey, options, keySet) => {
    const typesWithAny = [];
    let totalIssues = 0;
    let selectedIssues = 0;
    items.forEach((item) => {
      const allKeys = options.includeSubtasks
        ? keysForTypeItemWithSubtasks(item)
        : keysForTypeItem(item);
      totalIssues += allKeys.length;
      const count = allKeys.filter((k) => keySet.has(k)).length;
      selectedIssues += count;
      if (count > 0) typesWithAny.push(item.name);
    });
    return {
      [typesKey]: typesWithAny,
      [allKey]: totalIssues > 0 && selectedIssues === totalIssues,
    };
  };

  const emitChange = (patch) => {
    const merged = {
      ...DEFAULT_FILTER,
      ...filter,
      ...patch,
      all_statuses: true,
      statuses: [],
    };
    if (patch.selected_issue_keys) {
      const keySet = new Set(patch.selected_issue_keys);
      Object.assign(
        merged,
        deriveTypeFilterFromKeys(parents, 'all_parents', 'parent_types', {}, keySet),
        deriveTypeFilterFromKeys(
          children,
          'all_children',
          'child_types',
          { includeSubtasks: true },
          keySet,
        ),
      );
    }
    onChange?.(merged, { preview_jql: buildPreviewJql(projectKey, merged) });
  };

  const toggleIssueKey = (key, checked) => {
    const nextKeys = applyKeyPatch((set) => {
      if (checked) addWithDescendants(set, key);
      else removeWithDescendants(set, key);
    });
    emitChange({ selected_issue_keys: nextKeys });
  };

  const toggleAllVisible = (checked) => {
    const nextKeys = applyKeyPatch((set) => {
      if (checked) {
        browseTree.forEach((root) => {
          addWithDescendants(set, root.key);
        });
      } else {
        browseFlat.forEach((node) => {
          removeWithDescendants(set, node.key);
        });
      }
    });
    emitChange({ selected_issue_keys: nextKeys });
  };

  const toggleCollapsed = (scope, key) => {
    const setter = scope === 'browse' ? setCollapsedBrowse : setCollapsedSelected;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderIssueKey = (issue, { bold = true } = {}) => (
    <span style={{ color: typeColorMap[issue.issueType] || TEXT_PRIMARY, fontWeight: bold ? 600 : 500 }}>
      {issue.key}
    </span>
  );

  const renderColorLegend = () => (
    <div style={legendStyle}>
      <Text style={{ fontSize: 11, color: TEXT_MUTED, marginRight: 4 }}>Issue type colors:</Text>
      {issueTypeNames.map((name) => (
        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: typeColorMap[name],
              display: 'inline-block',
            }}
          />
          <span style={{ color: TEXT_PRIMARY }}>{name}</span>
        </span>
      ))}
    </div>
  );

  const renderIssueLabel = (node, { muted = false, viaParent = false } = {}) => (
    <span
      style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: muted ? TEXT_MUTED : TEXT_PRIMARY,
        display: 'block',
        paddingTop: 2,
      }}
    >
      {renderIssueKey(node, { bold: !muted })}
      {' — '}
      {node.title}
      <Text style={{ marginLeft: 6, fontSize: 11, color: TEXT_MUTED }}>
        ({node.issueType}{viaParent ? ', via parent' : ''})
      </Text>
    </span>
  );

  const renderCheckboxSlot = (node, { isSelected, ancestorSelected }) => {
    const descendantKeys = collectDescendantKeys(node.key, childrenByParent);
    const hasUnselectedDescendant = (
      isSelected
      && descendantKeys.length > 0
      && descendantKeys.some((key) => !selectedKeys.has(key))
    );
    return (
      <Checkbox
        checked={isSelected}
        indeterminate={hasUnselectedDescendant}
        disabled={disabled || loading}
        onChange={(e) => toggleIssueKey(node.key, e.target.checked)}
        title={
          ancestorSelected
            ? 'Included by parent selection; uncheck to exclude this issue'
            : undefined
        }
        style={{ margin: 0, padding: 0, lineHeight: 1 }}
      />
    );
  };

  const renderMonitorSlot = (node) => {
    if (!onToggleMonitor || isSubTaskIssue(node)) {
      return null;
    }
    const active = monitoredIssueKeys?.has?.(node.key);
    return (
      <Tooltip title={active ? 'Stop monitoring for Jira updates' : 'Monitor for Jira content updates'}>
        <span
          role="button"
          tabIndex={disabled || loading ? -1 : 0}
          aria-label={active ? `Stop monitoring ${node.key}` : `Monitor ${node.key} for updates`}
          aria-pressed={active}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled || loading) return;
            onToggleMonitor(node.key, !active);
          }}
          onKeyDown={(e) => {
            if (disabled || loading) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onToggleMonitor(node.key, !active);
            }
          }}
          style={{
            width: TREE_MONITOR_SLOT,
            height: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            color: active ? '#1677ff' : TEXT_MUTED,
            background: active ? 'rgba(22, 119, 255, 0.14)' : 'transparent',
            border: active ? '1px solid rgba(22, 119, 255, 0.45)' : '1px solid transparent',
            borderRadius: 4,
            opacity: disabled || loading ? 0.45 : 1,
            lineHeight: 1,
          }}
        >
          <RadarChartOutlined style={{ fontSize: 13, fontWeight: active ? 700 : 400 }} />
        </span>
      </Tooltip>
    );
  };

  const renderTreeNode = (node, {
    scope,
    depth = 0,
  }) => {
    const hasChildren = node.children.length > 0;
    const collapsedSet = scope === 'browse' ? collapsedBrowse : collapsedSelected;
    const expanded = hasChildren && !collapsedSet.has(node.key);
    const ancestorSelected = hasSelectedAncestor(node.key);
    const isSelected = selectedKeys.has(node.key);
    const viaParent = ancestorSelected;
    const mutedLabel = ancestorSelected;
    const showMonitor = scope === 'selected' && onToggleMonitor;

    return (
      <div key={node.key}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: showMonitor
              ? `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px ${TREE_MONITOR_SLOT}px minmax(0, 1fr)`
              : `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px minmax(0, 1fr)`,
            columnGap: TREE_COLUMN_GAP,
            alignItems: 'start',
            marginBottom: 4,
            paddingLeft: depth * TREE_DEPTH_INDENT,
          }}
        >
          <span
            style={{
              width: TREE_ARROW_WIDTH,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 5,
            }}
          >
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={() => toggleCollapsed(scope, node.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCollapsed(scope, node.key);
                  }
                }}
                style={{ cursor: 'pointer', color: typeColorMap[node.issueType], lineHeight: 1 }}
              >
                {expanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
              </span>
            ) : null}
          </span>
          <span
            style={{
              width: TREE_CHECKBOX_SLOT,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            {renderCheckboxSlot(node, { isSelected, ancestorSelected })}
          </span>
          {showMonitor ? (
            <span
              style={{
                width: TREE_MONITOR_SLOT,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 2,
              }}
            >
              {renderMonitorSlot(node)}
            </span>
          ) : null}
          <span style={{ minWidth: 0, paddingTop: 1 }}>
            {renderIssueLabel(node, { muted: mutedLabel, viaParent })}
          </span>
        </div>
        {hasChildren && expanded
          ? node.children.map((child) => renderTreeNode(child, { scope, depth: depth + 1 }))
          : null}
      </div>
    );
  };

  const browseSelectionState = useMemo(() => {
    const selectableRoots = browseTree.filter(
      (root) => !hasSelectedAncestor(root.key),
    );
    if (!selectableRoots.length) return { checked: false, indeterminate: false };
    const fullySelected = selectableRoots.filter((root) => selectedKeys.has(root.key)).length;
    return {
      checked: fullySelected === selectableRoots.length,
      indeterminate: fullySelected > 0 && fullySelected < selectableRoots.length,
    };
  }, [browseTree, hasSelectedAncestor, selectedKeys]);

  const paginateRoots = (roots, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return roots.slice(start, start + PAGE_SIZE);
  };

  const browsePageRoots = paginateRoots(browseTree, browsePage);
  const selectedPageRoots = paginateRoots(selectedTree, selectedPage);

  const selectedIssueCount = selectedKeys.size;

  const renderPagination = (total, page, onChange) => (
    <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(148, 22, 127, 0.1)', background: '#faf8fb' }}>
      <Pagination
        size="small"
        current={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={onChange}
        showSizeChanger={false}
        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} roots`}
        disabled={disabled || loading}
      />
    </div>
  );

  return (
    <div>
      {loading ? (
        <div style={loadingPanelStyle}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16, fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY }}>
            Loading Jira issues
          </Text>
          <Text style={{ display: 'block', marginTop: 6, fontSize: 13, color: TEXT_MUTED, maxWidth: 360, lineHeight: 1.55 }}>
            Fetching all issues for project {projectKey}…
          </Text>
        </div>
      ) : (
        <>
      {error ? (
        <div style={{ marginBottom: 8, color: '#cf1322', fontSize: 13 }}>{error}</div>
      ) : null}
      {!error && requireApiToken && !credentials?.jira_api_token ? (
        <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: TEXT_MUTED }}>
          Enter your API token and click Connect to load issues.
        </Text>
      ) : null}

      {issueTypeNames.length > 0 ? renderColorLegend() : null}

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={panelStyle}>
          <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
            Browse issues ({allIssues.length})
          </div>
          <Text style={{ display: 'block', padding: '2px 10px 6px', fontSize: 11, color: TEXT_MUTED }}>
            Showing all Jira issues. Select a parent to include its Sub-tasks automatically, or select Sub-tasks individually.
          </Text>
          <div style={filterBarStyle}>
            <Select
              size="small"
              value={typeFilter}
              disabled={disabled || loading}
              onChange={setTypeFilter}
              style={{ minWidth: 140, flex: '1 1 140px' }}
              options={[
                { value: '__all__', label: 'All issue types' },
                ...issueTypeNames.map((name) => ({ value: name, label: name })),
              ]}
            />
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />}
              placeholder="Search IDs…"
              value={searchText}
              disabled={disabled || loading}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: '1 1 200px' }}
            />
          </div>
          <div style={{ padding: '0 10px 6px' }}>
            <Checkbox
              checked={browseSelectionState.checked}
              indeterminate={browseSelectionState.indeterminate}
              disabled={disabled || loading || !browseTree.length}
              onChange={(e) => toggleAllVisible(e.target.checked)}
              style={{ color: TEXT_PRIMARY, fontSize: 12 }}
            >
              Select all visible ({selectableBrowseFlat.length})
            </Checkbox>
          </div>
          <div style={panelBodyStyle}>
            {browsePageRoots.length ? (
              browsePageRoots.map((node) => renderTreeNode(node, { scope: 'browse' }))
            ) : scopeLoaded ? (
              <div style={{ padding: '8px 4px' }}>
                <Text style={{ display: 'block', color: TEXT_PRIMARY, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                  {allIssues.length === 0 ? 'No eligible issues found' : 'No matching issues'}
                </Text>
                <Text style={{ color: TEXT_MUTED, fontSize: 12, lineHeight: 1.55 }}>
                  {browseEmptyMessage}
                </Text>
              </div>
            ) : null}
          </div>
          {browseTree.length > PAGE_SIZE
            ? renderPagination(browseTree.length, browsePage, setBrowsePage)
            : null}
        </div>

        <div style={panelStyle}>
          <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
            Selected issues ({selectedIssueCount})
          </div>
          <Text style={{ display: 'block', padding: '4px 10px 8px', fontSize: 11, color: TEXT_MUTED }}>
            Nested by parent-child hierarchy. Click the arrow to expand or collapse.
            {onToggleMonitor ? ' Use the monitor icon to watch for Jira content changes.' : ''}
          </Text>
          <div style={panelBodyStyle}>
            {selectedPageRoots.length ? (
              selectedPageRoots.map((node) => renderTreeNode(node, { scope: 'selected' }))
            ) : (
              <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>No issues selected yet</Text>
            )}
          </div>
          {selectedTree.length > PAGE_SIZE
            ? renderPagination(selectedTree.length, selectedPage, setSelectedPage)
            : null}
          {(selectedPanelFooter || onCheckMonitoredUpdates) ? (
            <div
              style={{
                padding: '8px 10px',
                borderTop: '1px solid rgba(148, 22, 127, 0.1)',
                background: '#faf8fb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {onCheckMonitoredUpdates ? (
                <Tooltip title={
                  monitoredIssueCount > 0
                    ? 'Compare monitored issues against Jira now'
                    : 'Enable monitoring on at least one issue first'
                }
                >
                  <Button
                    size="small"
                    icon={<SyncOutlined />}
                    loading={monitorCheckLoading}
                    disabled={disabled || loading || monitoredIssueCount === 0}
                    onClick={onCheckMonitoredUpdates}
                  >
                    Check for updates
                  </Button>
                </Tooltip>
              ) : <span />}
              {selectedPanelFooter ? (
                <div style={{ marginLeft: 'auto' }}>
                  {selectedPanelFooter}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {previewJql ? (
        <div style={{ fontSize: 12, color: TEXT_MUTED }}>
          <span style={{ color: TEXT_PRIMARY, fontWeight: 500 }}>
            {selectedIssueCount} issue{selectedIssueCount === 1 ? '' : 's'} selected
          </span>
          {' · '}
          JQL preview: <span style={{ color: TEXT_PRIMARY }}>{previewJql}</span>
        </div>
      ) : null}
        </>
      )}
    </div>
  );
};

export { DEFAULT_FILTER as DEFAULT_JIRA_ISSUE_TYPE_FILTER };
export default JiraIssueScopePicker;
